import * as pc from 'playcanvas';
import { box, colorMaterial } from './app';
import { loadPbrTextures, pbrMaterialUV, type PbrTextureUrls } from './materials';

/**
 * Photo-facade buildings — the "every visible pixel comes from a camera" art
 * direction (Blueprint DR-08 as amended 2026-07-08). Each building is a simple
 * massing box whose walls carry real photographed facade textures (ambientCG,
 * CC0 — public/textures/LICENSE.txt), the same projected-photo technique the
 * DR-10 "client-facade" tier uses for real customer buildings. Walls start as
 * neutral concrete-grey and the photo materials stream in asynchronously, so
 * cold boot never waits on textures.
 */

export interface FacadeSet {
  /** Folder under public/textures/. */
  dir: string;
  /** Window columns × rows contained in one texture tile. */
  bays: number;
  floors: number;
  /** Real-world meters of one bay / one floor, so windows read at human scale. */
  bayW: number;
  floorH: number;
  /** Set has an ao.jpg map. */
  ao: boolean;
  /** Snap tiling to whole bays/floors (window textures) vs. free tiling (plain surfaces). */
  snap: boolean;
}

function windowed(dir: string, bays: number, floors: number, bayW: number, floorH: number, ao = false): FacadeSet {
  return { dir, bays, floors, bayW, floorH, ao, snap: true };
}

function plain(dir: string, metersPerTile: number, ao = false): FacadeSet {
  return { dir, bays: 1, floors: 1, bayW: metersPerTile, floorH: metersPerTile, ao, snap: false };
}

/** Photographed facade sets (ambientCG Facade series, CC0). Bay/floor counts
 * were counted off the actual 2K textures — a wrong count makes the tiling cut
 * a window row at the wall top/bottom, which instantly breaks the photo look. */
export const FACADE_SETS = {
  glass: windowed('facade001', 13, 9, 2.0, 3.0),
  panel: windowed('facade004', 16, 12, 1.4, 2.8),
  office: windowed('facade006', 13, 8, 1.7, 3.2),
  bronze: windowed('facade012', 22, 18, 1.4, 3.0),
  grid: windowed('facade015', 26, 19, 1.5, 3.0),
  brick: windowed('facade018a', 6, 5, 2.0, 3.0, true),
  stone: windowed('facade019a', 7, 5, 2.6, 3.2, true),
  brown: windowed('facade020a', 8, 6, 1.9, 3.0, true),
  /** Bare painted-plaster shell for locked buildings ("closed doors", Blueprint §3.2). */
  wall: plain('wall', 5, true),
} as const;

const ROOF_SET = plain('roof', 7);

/** Which photo facades each building type draws from. */
export const TYPE_POOLS: Record<'school' | 'career' | 'company' | 'house', readonly FacadeSet[]> = {
  school: [FACADE_SETS.stone, FACADE_SETS.office, FACADE_SETS.panel],
  career: [FACADE_SETS.office, FACADE_SETS.glass, FACADE_SETS.panel],
  company: [FACADE_SETS.grid, FACADE_SETS.bronze, FACADE_SETS.glass],
  house: [FACADE_SETS.brick, FACADE_SETS.brown, FACADE_SETS.stone],
};

/** Picks a stable pool entry for an id, so the same building/house always renders the same variant. */
export function hashPick<T>(id: string, pool: readonly T[]): T {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return pool[Math.abs(h) % pool.length];
}

function urlsFor(set: FacadeSet): PbrTextureUrls {
  const base = `/textures/${set.dir}`;
  return {
    diffuse: `${base}/diffuse.jpg`,
    normal: `${base}/normal.jpg`,
    rough: `${base}/rough.jpg`,
    ao: set.ao ? `${base}/ao.jpg` : undefined,
  };
}

/** Tiling that always cuts on whole window rows/columns — a half window at a
 * parapet instantly breaks the photo illusion; a slightly stretched one doesn't. */
function facadeTiling(set: FacadeSet, width: number, height: number): pc.Vec2 {
  if (!set.snap) {
    return new pc.Vec2(width / set.bayW, height / set.floorH);
  }
  const u = Math.max(1, Math.round(width / set.bayW)) / set.bays;
  const v = Math.max(1, Math.round(height / set.floorH)) / set.floors;
  return new pc.Vec2(u, v);
}

const matCache = new Map<string, Promise<pc.StandardMaterial>>();

function facadeMaterial(
  app: pc.Application,
  set: FacadeSet,
  width: number,
  height: number,
  tint?: string,
): Promise<pc.StandardMaterial> {
  const tiling = facadeTiling(set, width, height);
  const key = `${set.dir}|${tiling.x.toFixed(3)}|${tiling.y.toFixed(3)}|${tint ?? ''}`;
  let pending = matCache.get(key);
  if (!pending) {
    pending = loadPbrTextures(app, urlsFor(set)).then((tex) => pbrMaterialUV(tex, tiling.x, tiling.y, tint));
    matCache.set(key, pending);
  }
  return pending;
}

const WALL_T = 0.45;
const PLACEHOLDER = '#a9adb3';

/** Shared entrance materials (glass door leaf, concrete surround). */
let doorGlassMat: pc.StandardMaterial | null = null;
let doorFrameMat: pc.StandardMaterial | null = null;

function entranceMaterials(): { glass: pc.StandardMaterial; frame: pc.StandardMaterial } {
  if (!doorGlassMat) {
    doorGlassMat = new pc.StandardMaterial();
    doorGlassMat.diffuse.set(0.08, 0.1, 0.12);
    doorGlassMat.specular.set(0.06, 0.06, 0.06);
    doorGlassMat.gloss = 0.85;
    doorGlassMat.update();
  }
  if (!doorFrameMat) {
    doorFrameMat = new pc.StandardMaterial();
    doorFrameMat.diffuse.set(0.62, 0.6, 0.56);
    doorFrameMat.specular.set(0.04, 0.04, 0.04);
    doorFrameMat.gloss = 0.35;
    doorFrameMat.update();
  }
  return { glass: doorGlassMat, frame: doorFrameMat };
}

export interface FacadeBuildingOptions {
  id: string;
  size: { x: number; y: number; z: number };
  pool: readonly FacadeSet[];
  /** Multiplies the photo texture — used to darken locked/closed buildings. */
  tint?: string;
  /** Adds the entrance (door + surround + canopy) on the local +Z face. Default true. */
  door?: boolean;
}

/**
 * Builds a photo-facade building: four wall slabs (each face tiled to whole
 * window bays/floors at real-world scale) capped by a roof slab whose side
 * band doubles as a cornice hiding the walls' cut top edge. Geometry is
 * instant; photo materials stream in and replace the neutral placeholder.
 */
export function spawnFacadeBuilding(app: pc.Application, opts: FacadeBuildingOptions): pc.Entity {
  const { size } = opts;
  const set = hashPick(opts.id, opts.pool);
  const root = new pc.Entity(`facade-${opts.id}`);
  const placeholder = colorMaterial(opts.tint ?? PLACEHOLDER);

  type Xyz = { x: number; y: number; z: number };
  const walls: Array<{ scale: Xyz; pos: Xyz; faceW: number }> = [
    // Front/back (±Z) run the full width; sides (±X) fill the gap between them.
    { scale: { x: size.x, y: size.y, z: WALL_T }, pos: { x: 0, y: size.y / 2, z: (size.z - WALL_T) / 2 }, faceW: size.x },
    { scale: { x: size.x, y: size.y, z: WALL_T }, pos: { x: 0, y: size.y / 2, z: -(size.z - WALL_T) / 2 }, faceW: size.x },
    { scale: { x: WALL_T, y: size.y, z: size.z - WALL_T * 2 }, pos: { x: (size.x - WALL_T) / 2, y: size.y / 2, z: 0 }, faceW: size.z },
    { scale: { x: WALL_T, y: size.y, z: size.z - WALL_T * 2 }, pos: { x: -(size.x - WALL_T) / 2, y: size.y / 2, z: 0 }, faceW: size.z },
  ];
  for (const cfg of walls) {
    const wall = box('wall', cfg.scale, cfg.pos, placeholder);
    root.addChild(wall);
    void facadeMaterial(app, set, cfg.faceW, size.y, opts.tint).then((mat) => {
      if (wall.render) wall.render.material = mat;
    });
  }

  const roof = box('roof', { x: size.x, y: 0.35, z: size.z }, { x: 0, y: size.y + 0.175, z: 0 }, placeholder);
  root.addChild(roof);
  void facadeMaterial(app, ROOF_SET, size.x, size.z, opts.tint).then((mat) => {
    if (roof.render) roof.render.material = mat;
  });

  // The building's face: a glass entrance centered on the local +Z wall —
  // surround, recessed double door and a canopy slab. Callers rotate the whole
  // building so this face (and the plot gate) address the street.
  if (opts.door !== false) {
    const { glass, frame } = entranceMaterials();
    const zFace = size.z / 2;
    root.addChild(box('door-surround', { x: 3.6, y: 3.3, z: 0.3 }, { x: 0, y: 1.65, z: zFace + 0.03 }, frame));
    root.addChild(box('door', { x: 2.4, y: 2.7, z: 0.18 }, { x: 0, y: 1.35, z: zFace + 0.16 }, glass));
    root.addChild(box('door-canopy', { x: 4.2, y: 0.16, z: 1.5 }, { x: 0, y: 3.35, z: zFace + 0.75 }, frame));
  }

  return root;
}
